import { useSelector } from 'react-redux';

/**
 * Checks whether an organization is entitled to a Discovery (Market Research) feature.
 * Entitlement requires BOTH Accessibility (`featureKey`) and
 * Visibility (`${featureKey}-visibility`) under the `discovery` module.
 *
 * @param {Array} permissions - Array of module objects or flattened permission strings.
 * @param {string} featureKey - Base submodule key (e.g. 'recommendations', 'code-crosswalk').
 * @returns {boolean} True if entitled, false otherwise (fail-closed).
 */
export const hasDiscoveryFeatureEntitlement = (permissions, featureKey) => {
  if (!featureKey || !permissions || !Array.isArray(permissions) || permissions.length === 0) {
    return false;
  }

  const visibilityKey = `${featureKey}-visibility`;

  if (typeof permissions[0] === 'string') {
    const permSet = new Set(permissions);
    return permSet.has(featureKey) && permSet.has(visibilityKey);
  }

  const discoveryModule = permissions.find((m) => m?.key === 'discovery');
  const subs = Array.isArray(discoveryModule?.submodules)
    ? discoveryModule.submodules
    : permissions.flatMap((m) => (Array.isArray(m?.submodules) ? m.submodules : []));

  const allSubs = new Set(subs);
  return allSubs.has(featureKey) && allSubs.has(visibilityKey);
};

/**
 * React hook for Discovery feature entitlement from Redux orgSettings.
 * Falls back to true if orgSettings is not in store (e.g. focused unit tests).
 *
 * @param {string} featureKey - Base submodule key.
 * @returns {boolean}
 */
export const useDiscoveryFeatureEntitled = (featureKey) => {
  const permissions = useSelector((state) => {
    if (!state || !('orgSettings' in state)) {
      return null;
    }
    return state.orgSettings?.permissions || [];
  });

  if (permissions === null) {
    return true;
  }

  return hasDiscoveryFeatureEntitlement(permissions, featureKey);
};
